import { Avatar, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { ArrowBackBlue, Download2 } from 'Images';
import { BGCircles } from './BGCircles';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import { getDisabilityConfirmationPdf } from 'API/disabilityConfirmation';
import { exportToPdf } from 'API';
import { useLoader } from 'Hooks/useLoader';
import { useState } from 'react';
import { downloadFile } from 'Utils';

interface IDownloadAndInfoButtons {
  title: string;
  attachmentId?: number;
  disabilityName?: string;
  moreInfoLink: string;
  englishAttachmentId?: number;
  onClick?: () => void;
}

export const DownloadAndInfoButtons = ({
  title,
  attachmentId,
  disabilityName,
  moreInfoLink,
  englishAttachmentId,
  onClick,
}: IDownloadAndInfoButtons) => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.DisabilityApprovals);
  const { loader, isLoading, setIsLoading } = useLoader('', {}, 20, false);
  const [loaderId, setLoaderId] = useState('');

  const downloadPdfHandler = async (isEnglish: boolean) => {
    setIsLoading(true);
    isEnglish ? setLoaderId('eng') : setLoaderId('heb');
    if (attachmentId && disabilityName) {
      let id =
        isEnglish && englishAttachmentId ? englishAttachmentId : attachmentId;

      const base64Result = await getDisabilityConfirmationPdf(id);
      if (base64Result?.file)
        downloadFile(base64Result.file, 'pdf', disabilityName);
    } else await exportToPdf();
    setIsLoading(false);
    setLoaderId('');
  };

  return (
    <Box minWidth={isDesktop ? '270px' : '100%'}>
      <Stack
        position='relative'
        overflow='hidden'
        direction='row'
        justifyContent='center'
        alignItems='center'
        bgcolor='#E7EDFF'
        borderRadius={isDesktop ? '100px' : '0 0 16px 16px'}
        padding='10px 24px'
        gap='10px'
        width={
          !moreInfoLink && !englishAttachmentId
            ? isDesktop
              ? '140px'
              : '100%'
            : undefined
        }
        ml={
          !moreInfoLink && !englishAttachmentId
            ? isDesktop
              ? 'auto'
              : undefined
            : undefined
        }
      >
        <Stack>
          <Button
            onKeyDown={onEnterOrEscapeKeyDown}
            onClick={onClick ? onClick : () => downloadPdfHandler(false)}
            sx={{ gap: '6px' }}
            aria-label={`${dictionary.forDownload} ${title}`}
          >
            {isLoading && loaderId === 'heb' ? (
              loader
            ) : (
              <>
                <Avatar
                  alt=''
                  src={Download2}
                  sx={{ width: '20px', height: '20px' }}
                />
                <Typography
                  variant='body5'
                  color='primary.main'
                  whiteSpace='nowrap'
                >
                  {englishAttachmentId
                    ? dictionary.hebrewDownload
                    : dictionary.download}
                </Typography>
              </>
            )}
          </Button>
        </Stack>
        {englishAttachmentId && (
          <>
            <Divider
              orientation='vertical'
              sx={{ height: '24px', borderColor: 'primary.shade_200' }}
            />
            <Stack>
              <Button
                onKeyDown={onEnterOrEscapeKeyDown}
                onClick={() => downloadPdfHandler(true)}
                sx={{ gap: '6px' }}
                aria-label={`${dictionary.forDownload} ${title} ${dictionary.inEnglish}`}
              >
                {isLoading && loaderId === 'eng' ? (
                  loader
                ) : (
                  <>
                    <Avatar
                      alt=''
                      src={Download2}
                      sx={{ width: '20px', height: '20px' }}
                    />
                    <Typography
                      variant='body5'
                      color='primary.main'
                      whiteSpace='nowrap'
                    >
                      {dictionary.englishDownload}
                    </Typography>
                  </>
                )}
              </Button>
            </Stack>
          </>
        )}
        {moreInfoLink && (
          <>
            <Divider
              orientation='vertical'
              sx={{ height: '24px', borderColor: 'primary.shade_200' }}
            />
            <Stack>
              <Button
                onKeyDown={onEnterOrEscapeKeyDown}
                onClick={() => window.open(moreInfoLink)}
                role='link'
                aria-label={`${dictionary.moreInfoAriaLabel} ${title}`}
              >
                <Typography
                  variant='body5'
                  color='primary.main'
                  whiteSpace='nowrap'
                >
                  {dictionary.moreInfo}
                </Typography>
                <Avatar
                  alt=''
                  src={ArrowBackBlue}
                  sx={{ width: '16px', height: '16px' }}
                />
              </Button>
            </Stack>
          </>
        )}
        {!isDesktop && <BGCircles />}
      </Stack>
    </Box>
  );
};
